import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { hostname } from "../../../utils";

interface MetaAdsIntegrationFormProps {
  editMode?: boolean;
  initialData?: {
    accessToken?: string;
    connectedAt?: string;
    userName?: string;
    adAccountCount?: number;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: {
    accessToken?: string;
    validated?: boolean;
    userName?: string;
    adAccountCount?: number;
  }) => void;
  getToken: () => Promise<string>;
  companyId: string;
}

/**
 * Meta Ads Integration Form using System User Access Token
 *
 * Users provide a System User Access Token from Meta Business Manager.
 * System User tokens never expire, making them ideal for server-to-server integrations.
 *
 * Reference: https://developers.facebook.com/docs/marketing-api/system-users
 */
export default function MetaAdsIntegrationForm({
  editMode = false,
  initialData,
  onValidationChange,
  onDataChange,
  getToken,
  companyId,
}: MetaAdsIntegrationFormProps) {
  const [accessToken, setAccessToken] = useState("");
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [adAccountCount, setAdAccountCount] = useState<number>(0);

  // Check if already connected (edit mode)
  useEffect(() => {
    if (editMode && initialData?.accessToken) {
      setValidated(true);
      setUserName(initialData.userName || "");
      setAdAccountCount(initialData.adAccountCount || 0);
    }
  }, [editMode, initialData]);

  // Update parent when state changes
  useEffect(() => {
    onValidationChange(validated);
    onDataChange({
      accessToken: accessToken.trim(),
      validated,
      userName,
      adAccountCount,
    });
  }, [validated, accessToken, userName, adAccountCount, onValidationChange, onDataChange]);

  // Validate the access token by making a test API call
  const handleValidate = async () => {
    if (!accessToken.trim()) {
      setError("Please enter your access token");
      return;
    }

    setValidating(true);
    setError("");

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/integrations/meta-ads/validate`,
        {
          accessToken: accessToken.trim(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.valid) {
        setValidated(true);
        setUserName(response.data.userName || "");
        setAdAccountCount(response.data.adAccountCount || 0);
      } else {
        setError(response.data.message || "Invalid access token");
      }
    } catch (err: any) {
      console.error("Error validating Meta Ads token:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to validate access token"
      );
    } finally {
      setValidating(false);
    }
  };

  // In edit mode with existing connection, show connected status
  if (editMode && validated) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">Meta Ads account connected</Typography>
          {initialData?.userName && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Connected as: {initialData.userName}
            </Typography>
          )}
          {initialData?.adAccountCount !== undefined && (
            <Typography variant="caption" display="block">
              Ad Accounts: {initialData.adAccountCount}
            </Typography>
          )}
          {initialData?.connectedAt && (
            <Typography variant="caption" display="block">
              Connected on:{" "}
              {new Date(initialData.connectedAt).toLocaleDateString()}
            </Typography>
          )}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          To reconnect with a different account, remove this integration and
          create a new one.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" component="div">
          <strong>Connect using an Access Token</strong>
          <br />
          <br />
          You can generate a long-lived access token that{" "}
          <strong>never expires</strong> using the Graph API Explorer.
          <br />
          <br />
          <strong>How to get your token:</strong>
          <ol style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
            <li>
              Go to the{" "}
              <Link
                href="https://developers.facebook.com/tools/explorer/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Graph API Explorer
              </Link>
            </li>
            <li>
              In the <strong>Meta App</strong> field, select your app
            </li>
            <li>
              In the <strong>User or Page</strong> field, select "User Token"
            </li>
            <li>
              Under <strong>Add a Permission</strong>, add:{" "}
              <code>ads_read</code>, <code>ads_management</code>
            </li>
            <li>
              Click <strong>Generate Access Token</strong>
            </li>
            <li>
              Click the info icon (i) next to your token, then click{" "}
              <strong>Open in Access Token Tool</strong>
            </li>
            <li>
              Click <strong>Extend Access Token</strong> to get a long-lived
              token (expires in 60 days or never)
            </li>
            <li>Copy the extended token and paste it below</li>
          </ol>
          <Link
            href="https://developers.facebook.com/docs/marketing-api/system-users"
            target="_blank"
            rel="noopener noreferrer"
          >
            Or use a System User token (recommended for production) →
          </Link>
        </Typography>
      </Alert>

      <TextField
        fullWidth
        label="Access Token"
        value={accessToken}
        onChange={(e) => {
          setAccessToken(e.target.value);
          setValidated(false);
          setError("");
        }}
        helperText="Paste your extended access token from the Graph API Explorer"
        required
        type="password"
        sx={{ mb: 2 }}
        disabled={validated}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!validated ? (
        <Button
          variant="contained"
          onClick={handleValidate}
          disabled={validating || !accessToken.trim()}
          startIcon={
            validating && <CircularProgress size={16} color="inherit" />
          }
          fullWidth
          size="large"
          sx={{ mb: 2 }}
        >
          {validating ? "Validating..." : "Validate Token"}
        </Button>
      ) : (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Token validated successfully! Click "Install" to save.
          </Typography>
          {userName && (
            <Typography variant="caption" display="block">
              Connected as: {userName}
            </Typography>
          )}
          {adAccountCount > 0 && (
            <Typography variant="caption" display="block">
              Found {adAccountCount} ad account(s)
            </Typography>
          )}
        </Alert>
      )}

      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="caption">
          <strong>Important:</strong> Use the "Extend Access Token" feature in
          the Access Token Debugger to get a long-lived token. For production
          use, consider using a System User token which never expires.
        </Typography>
      </Alert>
    </Box>
  );
}
